import * as React from 'react';
import SlickSlider, { ResponsiveObject } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { Wrapper, StyledArrow } from './styles';
import { Format } from '../../../utils/styles-constants';

interface SliderProps {
	slidesLength: number;
	currentSlideIndex: number;
	children: React.ReactNode;
	onSlideChange: (newIndex: number) => void;
}

enum BREAKPOINTS {
	SmartphonePortrait = 481,
	Desktop = 1200,
}

enum SLIDES_TO_SHOW {
	SmartphonePortrait = 1,
	SmartphoneLandscape = 5,
	Tablet = 3,
	Desktop = 5,
}

const IS_PHONE = window.matchMedia(Format.Smartphone).matches || window.matchMedia(Format.SmartphoneLandscape).matches;
// manually set slide width for correct display when there are few items in slider
const SLIDES_LENGTH_TO_CORRECT_WIDTH = 5;

const getOptimalSlidesToShow = (slidesLength: number, breakpointSlidesToShow: SLIDES_TO_SHOW): number => (
	Math.min(slidesLength - 1 || 1, breakpointSlidesToShow)
);

const getResponsiveConfig = (slidesLength: number): ResponsiveObject[] => ([
	{
		breakpoint: BREAKPOINTS.Desktop,
		settings: {
			slidesToShow: getOptimalSlidesToShow(slidesLength, IS_PHONE ? SLIDES_TO_SHOW.SmartphoneLandscape : SLIDES_TO_SHOW.Tablet),
		},
	},
	{
		breakpoint: BREAKPOINTS.SmartphonePortrait,
		settings: {
			slidesToShow: SLIDES_TO_SHOW.SmartphonePortrait,
		},
	},
]);

const getSlidesToShow = (slidesLength: number, sliderWidth: number): number => {
	if (sliderWidth < BREAKPOINTS.SmartphonePortrait) {
		return SLIDES_TO_SHOW.SmartphonePortrait;
	} else if (sliderWidth >= BREAKPOINTS.Desktop) {
		return getOptimalSlidesToShow(slidesLength, SLIDES_TO_SHOW.Desktop);
	} else {
		return getOptimalSlidesToShow(slidesLength, IS_PHONE ? SLIDES_TO_SHOW.SmartphoneLandscape : SLIDES_TO_SHOW.Tablet);
	}
};

const getSlideWidth = (sliderWidth: number, sliderHeight: number, slidesLength: number): number => {
	const MAX_SLIDE_WIDTH = 350;
	const SCALE_COEF = 1.3; // is needed as center slide is scaled

	let width = sliderWidth / getSlidesToShow(slidesLength, sliderWidth);

	if (width > sliderHeight) {
		width = sliderHeight;
	}

	width = width / SCALE_COEF;
	width = Math.min(width, MAX_SLIDE_WIDTH);

	return width;
};

const Arrow = ({ className, style, onClick, direction }: React.SVGProps<SVGSVGElement>) => (
	<StyledArrow
		className={className}
		style={{...style}}
		onClick={onClick}
	>
		<use xlinkHref={`#svgicon-iconset_slider-${direction}`} />
	</StyledArrow>
);

const Slider = ({
	slidesLength,
	currentSlideIndex,
	children,
	onSlideChange,
}: SliderProps) => {
	// is needed to reinit SlickSlider on resize
	const [sliderKey, setSliderKey] = React.useState('reinit');
	const isSlideClicked = React.useRef(false);

	const handleMouseDown = () => {
		isSlideClicked.current = true;
	};

	const handleMouseMove = () => {
		if (isSlideClicked.current) {
			document.body.classList.add('is-dragging');
		}
	};

	const handleMouseUp = () => {
		if (isSlideClicked.current) {
			document.body.classList.remove('is-dragging');
			isSlideClicked.current = false;
		}
	};

	const setSlidesWidth = () => {
		const slider: HTMLDivElement | null = document.querySelector('.slick-slider');

		if (!slider || slidesLength > SLIDES_LENGTH_TO_CORRECT_WIDTH) {
			return;
		}

		const { clientWidth, clientHeight } = slider;
		const slideWidth = getSlideWidth(clientWidth, clientHeight, slidesLength);
		const slides = slider.querySelectorAll('.slick-slide');

		slides.forEach((slide: HTMLDivElement) => {
			slide.style.width = `${slideWidth}px`;
		});

		if (slidesLength === 2) {
			// to not show a lot of duplicated items on wide screen
			const correctedSliderWidth = slideWidth * 3;
			if (correctedSliderWidth < clientWidth) {
				const correctionCoef = clientWidth > BREAKPOINTS.Desktop ? 15 : 2;
				const padding = (clientWidth - correctedSliderWidth) / 2 - correctionCoef;
				slider.style.padding = `0 ${padding}px`;
			}
		}
	};

	React.useEffect(() => {
		window.addEventListener('mouseup', handleMouseUp);

		return () => {
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, []);

	React.useEffect(() => {
		if (slidesLength > SLIDES_LENGTH_TO_CORRECT_WIDTH) {
			return;
		}

		setSlidesWidth();

		const handleResize = () => {
			setSliderKey(`${sliderKey}-${Date.now()}`);
		};

		// Standard window 'resize' event isn't triggered in webview on iOS13, it should be listened on window.visualViewport
		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', handleResize);
		} else {
			window.addEventListener('resize', handleResize);
		}

		return () => {
			if (window.visualViewport) {
				window.visualViewport.removeEventListener('resize', handleResize);
			} else {
				window.removeEventListener('resize', handleResize);
			}
		};
	}, [slidesLength]);

	return (
		<Wrapper
			slidesLength={slidesLength}
			currentSlideIndex={currentSlideIndex}
			onMouseDown={handleMouseDown}
			onMouseMove={handleMouseMove}
			onMouseUp={handleMouseUp}
		>
			<SlickSlider
				key={sliderKey}
				variableWidth={slidesLength <= SLIDES_LENGTH_TO_CORRECT_WIDTH}
				centerMode={true}
				slidesToShow={getOptimalSlidesToShow(slidesLength, SLIDES_TO_SHOW.Desktop)}
				dots={false}
				infinite={true}
				speed={500}
				lazyLoad={slidesLength > SLIDES_LENGTH_TO_CORRECT_WIDTH ? 'ondemand' : undefined}
				swipeToSlide={true}
				initialSlide={currentSlideIndex}
				responsive={getResponsiveConfig(slidesLength)}
				prevArrow={<Arrow direction="previous" />}
				nextArrow={<Arrow direction="next" />}
				onReInit={setSlidesWidth}
				afterChange={onSlideChange}
			>
				{children}
			</SlickSlider>
		</Wrapper>
	);
};

export default Slider;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/CoverFlow/Slider/index.tsx